import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  Building2Icon,
  GaugeIcon,
  SparklesIcon,
  TargetIcon,
} from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { config, isDevMode } from "@/shared/lib/config";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { toISOString } from "@/shared/lib/date-time-performance";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormDateQuarterTimeField } from "@/shared/ui/FormDateQuarterTimeField";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  PRIORITY_ORDER_NONE_VALUE,
  PRIORITY_ORDER_SELECT_OPTIONS,
} from "../lib/priority-order";
import { useEventCategoriesLookup } from "../api/queries";
import { PriorityOrderBadge } from "./PriorityOrderBadge";
import { CustomerNameAutocomplete } from "./CustomerNameAutocomplete";
import { EventDateConflictAlert } from "./EventDateConflictAlert";
import { FormRequestShowDatesField } from "./FormRequestShowDatesField";
import { useEventDateConflictCheck } from "../lib/use-event-date-conflict-check";

type UserItem = { id: string; name: string; email?: string | null };

const channelOptions = [
  { value: "phone", label: "ໂທລະສັບ" },
  { value: "facebook", label: "Facebook" },
  { value: "walk_in", label: "ເຂົ້າມາຕິດຕໍ່" },
  { value: "referral", label: "ແນະນຳ" },
  { value: "other", label: "ອື່ນໆ" },
] as const;

const statusOptions = [
  { value: "new", label: "ໃໝ່" },
  { value: "negotiating", label: "ກຳລັງເຈລະຈາ" },
  { value: "pending_quote", label: "ລໍຖ້າໃບສະເໜີລາຄາ" },
  { value: "won", label: "ຢືນຢັນແລ້ວ" },
  { value: "lost", label: "ເສຍໂອກາດ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
] as const;

const dateModeOptions = [
  { value: "event_date", label: "ວັນທີ່ຈັດງານ (ວັນດຽວ)" },
  { value: "show_dates", label: "ວັນທີ່ໂຊ (ຫຼາຍວັນ)" },
] as const;

const RequestFormSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຫົວຂໍ້"),
    customerName: z.string().nullable().optional(),
    contactName: z.string().min(1, "ຕ້ອງປ້ອນຊື່ຜູ້ຕິດຕໍ່"),
    contactPhone: z.string().nullable().optional(),
    contactEmail: z.string().nullable().optional(),
    contactAddress: z.string().nullable().optional(),
    channel: z.enum([
      "phone",
      "line",
      "facebook",
      "walk_in",
      "referral",
      "other",
    ]),
    status: z.enum([
      "new",
      "negotiating",
      "pending_quote",
      "won",
      "lost",
      "cancelled",
    ]),
    categoryId: z.string().nullable().optional(),
    dateMode: z.enum(["event_date", "show_dates"]),
    requestedEventDate: z.string().nullable().optional(),
    showDates: z.array(z.object({ value: z.string() })).optional(),
    priorityOrder: z.string().nullable().optional(),
    assignedTo: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.dateMode === "show_dates") {
      const dates = (vals.showDates ?? []).map((row) => row.value.trim()).filter(Boolean);
      if (dates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["showDates"],
          message: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ວັນໂຊ",
        });
      }
    }
  });

export type RequestFormValues = z.infer<typeof RequestFormSchema>;

/** Payload ที่ส่งออกจากฟอร์ม — priorityOrder เป็น number | null */
export type RequestFormSubmitValues = Omit<
  RequestFormValues,
  "priorityOrder" | "showDates"
> & {
  priorityOrder: number | null;
  showDates?: string[];
};

function priorityOrderToFormValue(
  value: number | string | null | undefined,
): string {
  if (value == null || value === "" || value === PRIORITY_ORDER_NONE_VALUE) {
    return PRIORITY_ORDER_NONE_VALUE;
  }
  return String(value);
}

function priorityOrderFromFormValue(
  value: string | null | undefined,
): number | null {
  if (
    value == null ||
    value === "" ||
    value === PRIORITY_ORDER_NONE_VALUE
  ) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildDefaultShowDates(
  initial?: Array<{ value: string }> | string[] | null,
): Array<{ value: string }> {
  if (!initial?.length) return [{ value: "" }];
  if (typeof initial[0] === "string") {
    return (initial as string[]).map((value) => ({ value }));
  }
  return initial as Array<{ value: string }>;
}

type RequestFormProps = {
  initialValues?: Partial<
    Omit<RequestFormValues, "priorityOrder" | "showDates"> & {
      priorityOrder?: number | string | null;
      showDates?: Array<{ value: string }> | string[];
    }
  >;
  onSubmit: (values: RequestFormSubmitValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
  /** แก้ไขคิวเดิม — ไม่นับ conflict กับตัวเอง */
  excludeRequestId?: string;
  /** งานที่แปลงจากคิวนี้ — ไม่นับ conflict กับงานต้นทาง */
  excludeEventId?: string;
};

function getOptionLabel<T extends readonly { value: string; label: string }[]>(
  options: T,
  value: string | null | undefined,
) {
  return options.find((option) => option.value === value)?.label ?? "-";
}

export function RequestForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  excludeRequestId,
  excludeEventId,
}: RequestFormProps) {
  const methods = RHF.useForm<RequestFormValues>({
    resolver: zodResolver(RequestFormSchema),
    defaultValues: {
      title: initialValues?.title ?? "",
      customerName: initialValues?.customerName ?? null,
      contactName: initialValues?.contactName ?? "",
      contactPhone: initialValues?.contactPhone ?? null,
      contactEmail: initialValues?.contactEmail ?? null,
      contactAddress: initialValues?.contactAddress ?? null,
      channel: initialValues?.channel ?? "phone",
      status: initialValues?.status ?? "new",
      categoryId: initialValues?.categoryId ?? null,
      dateMode: initialValues?.dateMode ?? "event_date",
      requestedEventDate:
        toDateTimeLocalValue(initialValues?.requestedEventDate) || null,
      showDates: buildDefaultShowDates(initialValues?.showDates),
      priorityOrder: priorityOrderToFormValue(initialValues?.priorityOrder),
      assignedTo: initialValues?.assignedTo ?? null,
      notes: initialValues?.notes ?? null,
    },
  });

  const titleValue = methods.watch("title");
  const customerNameValue = methods.watch("customerName");
  const contactNameValue = methods.watch("contactName");
  const channelValue = methods.watch("channel");
  const statusValue = methods.watch("status");
  const priorityValue = methods.watch("priorityOrder");
  const dateModeValue = methods.watch("dateMode");
  const requestedDateValue = methods.watch("requestedEventDate");
  const showDatesValue = methods.watch("showDates");

  const categoryLookup = useEventCategoriesLookup();
  const categoryOptions = useMemo(
    () =>
      (categoryLookup.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [categoryLookup.data?.items],
  );

  const conflictEventDate = useMemo(() => {
    if (dateModeValue === "event_date") return requestedDateValue;
    const dates = (showDatesValue ?? [])
      .map((row) => row.value.trim())
      .filter(Boolean)
      .sort();
    if (dates.length === 0) return null;
    return dates[0] ?? null;
  }, [dateModeValue, requestedDateValue, showDatesValue]);

  const conflictEndDate = useMemo(() => {
    if (dateModeValue !== "show_dates") return undefined;
    const dates = (showDatesValue ?? [])
      .map((row) => row.value.trim())
      .filter(Boolean)
      .sort();
    if (dates.length <= 1) return undefined;
    return dates[dates.length - 1];
  }, [dateModeValue, showDatesValue]);

  const { conflicts: dateConflicts, isChecking: isCheckingDateConflicts } =
    useEventDateConflictCheck({
      eventDate: conflictEventDate,
      endDate: conflictEndDate,
      excludeRequestId,
      excludeEventId,
    });

  const handleAutoFill = () => {
    methods.reset({
      title: "ງານສະແດງງານແຕ່ງງານ",
      customerName: "Mekong Events",
      contactName: "ນາງ ມາລີ",
      contactPhone: "+856 20 5555 1234",
      contactEmail: "mali@example.com",
      contactAddress: "ບ້ານໂພນທັນ, ເມືອງໄຊເສດຖາ, ນະຄອນຫຼວງວຽງຈັນ",
      channel: "line",
      status: "negotiating",
      categoryId: categoryOptions[0]?.value ?? null,
      dateMode: "event_date",
      requestedEventDate: toDateTimeLocalValue(
        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      ),
      showDates: [{ value: "" }],
      priorityOrder: "1",
      assignedTo: null,
      notes: "ລູກຄ້າຕ້ອງການທີມສະແດງ 3 ຄົນ, ລະບົບສຽງ ແລະ ແສງເວທີ.",
    });
  };

  const showDatesCount =
    dateModeValue === "show_dates"
      ? (showDatesValue ?? []).filter((row) => row.value.trim()).length
      : 0;

  return (
    <FormRoot
      methods={methods}
      onSubmit={(values) =>
        onSubmit({
          ...values,
          priorityOrder: priorityOrderFromFormValue(values.priorityOrder),
          categoryId: values.categoryId || null,
          requestedEventDate:
            values.dateMode === "event_date" && values.requestedEventDate
              ? toISOString(new Date(values.requestedEventDate))
              : null,
          showDates:
            values.dateMode === "show_dates"
              ? (values.showDates ?? [])
                  .map((row) => row.value.trim())
                  .filter(Boolean)
                  .map((value) => toISOString(new Date(value)))
              : undefined,
          assignedTo: values.assignedTo || null,
        })
      }
    >
      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="border-red-700 border-l-2 bg-background px-6 py-7 shadow-sm md:px-9">
          <div className="space-y-7">
            <div>
              <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                ຫົວຂໍ້ຄຳຂໍ <span className="text-red-700">*</span>
              </p>
              <FormInput
                name="title"
                placeholder="ງານສະແດງງານແຕ່ງງານ"
                requiredMark
              />
            </div>

            <div>
              <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                ລູກຄ້າ
              </p>
              <CustomerNameAutocomplete />
            </div>

            <div>
              <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                ທີ່ຢູ່ລູກຄ້າ
              </p>
              <FormTextarea
                name="contactAddress"
                rows={3}
                placeholder="ທີ່ຢູ່ສຳລັບສະແດງໃນເອກະສານ..."
              />
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ຜູ້ປະສານງານ <span className="text-red-700">*</span>
                </p>
                <FormInput
                  name="contactName"
                  placeholder="ຊື່ຜູ້ປະສານງານ"
                  requiredMark
                />
              </div>
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ຊ່ອງທາງ
                </p>
                <FormSelect name="channel" options={[...channelOptions]} />
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ເບີໂທ
                </p>
                <FormInput name="contactPhone" placeholder="+856..." />
              </div>
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ອີເມວ
                </p>
                <FormInput
                  name="contactEmail"
                  placeholder="customer@example.com"
                />
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ຫມວດງານ
                </p>
                <FormSelect
                  name="categoryId"
                  options={categoryOptions}
                  placeholder="ເລືອກຫມວດງານ"
                />
              </div>
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ຜູ້ຮັບຜິດຊອບ
                </p>
                <FormInfiniteCombobox<UserItem>
                  name="assignedTo"
                  placeholder="ເລືອກຜູ້ໃຊ້..."
                  queryKey={["performance-requests", "assignees", "lookup"]}
                  queryFn={({ search, pageParam }) =>
                    fetchLookupForInfinite<UserItem>(
                      `${config.apiUrl}/requests/assignees/lookup`,
                      { search, pageParam },
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<UserItem>(
                      `${config.apiUrl}/requests/assignees/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) =>
                    item.email ? `${item.name} (${item.email})` : item.name
                  }
                  getValue={(item) => item.id}
                />
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ສະຖານະ
                </p>
                <FormSelect name="status" options={[...statusOptions]} />
              </div>
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ຄວາມສຳຄັນ
                </p>
                <FormSelect
                  name="priorityOrder"
                  options={[...PRIORITY_ORDER_SELECT_OPTIONS]}
                />
              </div>
            </div>

            <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
              <div>
                <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                  ວັນທີ່ລູກຄ້າຕ້ອງການຈັດງານ
                </p>
                <FormSelect name="dateMode" options={[...dateModeOptions]} />
              </div>

              {dateModeValue === "event_date" ? (
                <div className="space-y-3">
                  <FormDateQuarterTimeField
                    name="requestedEventDate"
                    allowEmpty
                  />
                  <EventDateConflictAlert
                    conflicts={dateConflicts}
                    isChecking={isCheckingDateConflicts}
                  />
                  <p className="text-muted-foreground text-xs">
                    ວັນທີ່ຈັດງານຫຼັກ — ບໍ່ແມ່ນການເລື່ອນງານທີ່ແປງແລ້ວ
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <FormRequestShowDatesField />
                  <EventDateConflictAlert
                    conflicts={dateConflicts}
                    isChecking={isCheckingDateConflicts}
                  />
                  <p className="text-muted-foreground text-xs">
                    ເລືອກທຸກວັນໂຊ — ເມື່ອແປງເປັນງານຈະ bulk ສ້າງຕາຕະລາງໂຊອັດຕະໂນມັດ
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-7">
              <p className="mb-3 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
                ໝາຍເຫດ
              </p>
              <FormTextarea
                name="notes"
                rows={7}
                placeholder="ຄວາມຕ້ອງການຂອງລູກຄ້າ, ງົບປະມານ, ລາຍລະອຽດສະຖານທີ່..."
              />
            </div>
          </div>

          <div className="mt-9 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
            <div className="flex items-center gap-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={submitting}
                  className="min-w-32"
                >
                  ຍົກເລີກ
                </Button>
              )}
              {isDevMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAutoFill}
                  disabled={submitting}
                >
                  <SparklesIcon className="size-4" />
                  ຕົວຢ່າງ
                </Button>
              )}
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="min-w-48 bg-red-700 font-bold tracking-wider hover:bg-red-800"
            >
              {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຄຳຂໍ"}
            </Button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-muted/60 p-6">
            <p className="mb-5 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
              ຂໍ້ມູນລູກຄ້າ
            </p>
            <div className="flex items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center bg-background text-red-700">
                <Building2Icon className="size-5" />
              </div>
              <div>
                <p className="font-bold text-base">
                  {customerNameValue || contactNameValue || "ຍັງບໍ່ໄດ້ລະບຸລູກຄ້າ"}
                </p>
                <p className="mt-1 text-muted-foreground text-xs">
                  {customerNameValue && contactNameValue ? contactNameValue : titleValue || "ລາຍລະອຽດຄຳຂໍຈະສະແດງຢູ່ນີ້"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="bg-background px-2 py-1 font-bold text-[10px] uppercase tracking-wider">
                    {getOptionLabel(statusOptions, statusValue)}
                  </span>
                  <span className="bg-background px-2 py-1 font-bold text-[10px] uppercase tracking-wider">
                    {getOptionLabel(channelOptions, channelValue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-red-700 border-t-2 bg-muted/60 p-6">
            <p className="mb-5 font-bold text-[11px] text-muted-foreground uppercase tracking-[0.28em]">
              ຂໍ້ກຳນົດການດຳເນີນງານ
            </p>
            <div className="divide-y text-sm">
              <div className="flex items-center justify-between py-3">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <TargetIcon className="size-4" />
                  ຄວາມສຳຄັນ
                </span>
                <PriorityOrderBadge
                  priorityOrder={priorityOrderFromFormValue(priorityValue)}
                />
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <GaugeIcon className="size-4" />
                  ວັນທີຕ້ອງການ
                </span>
                <span className="font-bold">
                  {dateModeValue === "show_dates"
                    ? showDatesCount > 0
                      ? `${showDatesCount} ວັນໂຊ`
                      : "-"
                    : requestedDateValue
                      ? "ຕັ້ງແລ້ວ"
                      : "-"}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-muted-foreground">ການແປງເປັນງານ</span>
                <span className="font-bold text-red-700">
                  {statusValue === "won" ? "ພ້ອມແປງ" : "ຢູ່ໃນຄິວ"}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </FormRoot>
  );
}
